import {useCallback, useState} from "react";
import {AxiosError} from "axios";

// Api
import {ChangePassword} from "../../Api/Account";

type UsePasswordUpdateArgs = {
    getValidAccessToken: () => Promise<string | null>;
};

type PasswordUpdateResult =
    | { ok: true }
    | { ok: false; code?: string; status?: number; error?: AxiosError | Error };

function extractErrorCode(err: AxiosError): { code?: string; status?: number } {
    const
        status = err.response?.status,
        data = err.response?.data as any;

    if (!data) return {status};

    if (typeof data === "string") {
        // backend might just write the code as a string
        return {code: data, status};
    }

    if (typeof data.error === "string") {
        return {code: data.error, status};
    }

    return {status};
}

function mapErrorCodeToMessage(code?: string, status?: number): string {
    if (!code) {
        if (status === 401) return "Your session has expired. Please log in again.";
        return "Something went wrong while updating your password.";
    }

    switch (code) {
        case "invalid_current_password":
            return "Your current password is incorrect.";
        case "weak_password":
            return "Your new password is too weak. It must be at least 8 characters long.";
        case "password_mismatch":
            return "New password and confirmation do not match.";
        case "user_not_found":
            return "Account not found.";
        case "invalid_token":
            return "Your session has expired. Please log in again.";

        // treat internal codes as generic
        case "password_verify_failed":
        case "password_hash_failed":
        case "db_error":
        case "db_set_password_failed":
            return "Something went wrong while updating your password.";

        default:
            return "Something went wrong while updating your password.";
    }
}

export function useUpdatePassword({getValidAccessToken}: UsePasswordUpdateArgs) {
    const
        [isUpdating, setIsUpdating] = useState(false),
        [errorMessage, setErrorMessage] = useState<string | null>(null),
        [success, setSuccess] = useState(false),

        clearError = useCallback(() => setErrorMessage(null), []),
        clearSuccess = useCallback(() => setSuccess(false), []),

        changePassword = useCallback(
            async (
                currentPassword: string,
                newPassword: string,
                confirmPassword: string
            ): Promise<PasswordUpdateResult> => {
                const token = await getValidAccessToken();
                if (!token) {
                    const err = new Error("Not authenticated");
                    setErrorMessage("You must be logged in to change your password.");
                    setSuccess(false);
                    return {ok: false, error: err};
                }

                const controller = new AbortController();
                setIsUpdating(true);
                setErrorMessage(null);
                setSuccess(false);

                try {
                    const res = await ChangePassword(
                        currentPassword,
                        newPassword,
                        confirmPassword,
                        token,
                        controller
                    );

                    if (res === true) {
                        setSuccess(true);
                        return {ok: true};
                    }

                    const axiosErr = res as AxiosError;
                    console.log("ChangePassword error:", axiosErr.response?.status, axiosErr.response?.data);
                    const
                        {code, status} = extractErrorCode(axiosErr),
                        msg = mapErrorCodeToMessage(code, status);

                    setErrorMessage(msg);
                    setSuccess(false);


                    return {ok: false, code, status, error: axiosErr};
                } catch (err) {
                    setErrorMessage("Something went wrong while updating your password.");
                    setSuccess(false);
                    return {ok: false, error: err as Error};
                } finally {
                    controller.abort();
                    setIsUpdating(false);
                }
            },
            [getValidAccessToken]
        );

    return {
        changePassword,
        isUpdating,
        errorMessage,
        success,
        clearError,
        clearSuccess,
    };
}
